import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Radio, Zap, ListVideo, Coins, BadgeCheck, Search } from "lucide-react";
import { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { FEED, DROPS } from "../constants/demoData";
import { CREATORS, CREATOR_TAGS } from "../constants/creators";

const ease = [0.16, 1, 0.3, 1];
const initials = (n) => n.split(" ").map((w) => w[0]).join("");
export const usd = (tokens) => `$${(tokens * 0.00933).toFixed(2)} USD`;

export const CreatorChip = ({ c, small = false }) => (
  <span className="flex items-center gap-2">
    <span className={`${small ? "h-6 w-6 text-[0.55rem]" : "h-8 w-8 text-xs"} inline-flex items-center justify-center rounded-full bg-gradient-to-br ${c.grad} font-display font-extrabold text-white`}>{initials(c.name)}</span>
    <span className={`${small ? "text-xs" : "text-sm"} font-medium text-ink`}>{c.name}</span>
    <BadgeCheck size={13} className="text-violet-600" />
  </span>
);

export const PriceButton = ({ tokens, label, testId, variant = "primary" }) => {
  const { t } = useLang();
  return (
    <button data-testid={testId} onClick={() => toast(t.demo.toastPay)} className={`pill inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold ${variant === "primary" ? "btn-primary" : "btn-ghost"}`}>
      {tokens ? <Coins size={13} className="text-amber-400" /> : <Lock size={13} />}
      {label}{tokens ? ` · ${tokens.toLocaleString("es-CO")}` : ""}
    </button>
  );
};

export const DemoHome = () => {
  const { t, lang } = useLang();
  const h = t.demo.home;
  const live = FEED[0];
  const drop = FEED[1];
  return (
    <div data-testid="demo-home" className="space-y-10 pb-20">
      <div className="grid gap-4 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="hero-card grain relative overflow-hidden rounded-[1.75rem] border border-white/70 p-8 lg:col-span-8 lg:p-10">
          <div className="relative">
            <span className="eyebrow inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/70 px-3 py-1 text-violet-700"><Zap size={11} /> {h.eyebrow}</span>
            <h1 data-testid="demo-home-headline" className="font-display mt-6 text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-ink sm:text-5xl lg:text-6xl">
              <span className="block">{h.line1}</span>
              <span className="text-gradient block">{h.line2}</span>
            </h1>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-ink-2 sm:text-base">{h.sub}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/app/discover" data-testid="demo-home-discover-cta" className="pill btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold"><Zap size={15} /> {h.cta1}</Link>
              <Link to="/app/collections" className="pill btn-ghost inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold"><ListVideo size={15} /> {h.cta2}</Link>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
              {t.hero.steps.map((s, i) => (
                <li key={s} className="eyebrow flex items-center gap-1.5 text-[0.6rem] text-ink-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 ? "#ec4899" : "#7c3aed" }} />{s}</li>
              ))}
            </ul>
          </div>
        </motion.div>
        <div className="flex flex-col gap-4 lg:col-span-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease }} className="card-soft overflow-hidden">
            <div className={`relative h-36 bg-gradient-to-br ${live.creator.grad}`}>
              <span className="eyebrow absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[0.58rem] text-ink"><Radio size={11} className="text-pink-500" /> {h.upcomingLive} · 18 sep, 19:00</span>
            </div>
            <div className="p-4">
              <CreatorChip c={live.creator} small />
              <p className="font-display mt-2 text-base font-bold text-ink">{live.title[lang]}</p>
              <p className="mt-1 text-xs text-mist">{h.subscribers}</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease }} className="card-soft p-4">
            <div className="flex items-center gap-4">
              <div className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br ${drop.creator.grad}`}><Lock size={14} className="absolute bottom-2 right-2 text-white/80" /></div>
              <div className="min-w-0 flex-1">
                <span className="eyebrow inline-flex items-center gap-1.5 rounded-full bg-pink-100 px-2 py-0.5 text-[0.55rem] text-pink-700"><span className="h-1.5 w-1.5 rounded-full bg-pink-500" /> {h.activeDrop}</span>
                <p className="font-display mt-1.5 text-sm font-bold leading-tight text-ink">{drop.title[lang]}</p>
                <p className="mt-0.5 text-xs text-mist"><Coins size={11} className="mr-1 inline text-amber-500" />{drop.price} · {usd(drop.price)} · {drop.units} {h.units}</p>
              </div>
            </div>
            <div className="mt-3 flex justify-end"><PriceButton tokens={drop.price} label={h.unlock} testId="demo-drop-unlock" /></div>
          </motion.div>
        </div>
      </div>

      <section>
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-ink">⚡ {h.dropsTitle}</h2>
            <p className="text-xs text-mist">{h.dropsSub}</p>
          </div>
          <Link to="/app/discover" className="text-xs font-semibold text-violet-700">{h.viewAll} →</Link>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {DROPS.map((dr, i) => (
            <motion.div key={dr.id} data-testid={`demo-drop-${dr.id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease }} className="card-soft overflow-hidden">
              <div className={`relative h-36 bg-gradient-to-br ${dr.creator.grad} ${dr.status === "soldout" ? "grayscale" : ""}`}>
                <span className={`eyebrow absolute left-3 top-3 rounded-full px-2 py-0.5 text-[0.55rem] ${dr.status === "live" ? "bg-rose-500 text-white" : dr.status === "presale" ? "bg-amber-300 text-ink" : "bg-ink text-white"}`}>
                  {dr.status === "live" ? `● ${h.live}` : dr.status === "presale" ? h.presale : h.soldOut}
                </span>
                <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2 py-0.5 font-mono text-[0.6rem] font-semibold text-ink">{dr.units} {h.units}</span>
              </div>
              <div className="p-4">
                <CreatorChip c={dr.creator} small />
                <p className="font-display mt-2 text-sm font-bold text-ink">{dr.title[lang]}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-mist"><Coins size={11} className="mr-1 inline text-amber-500" />{dr.price} · {usd(dr.price)}</span>
                  {dr.status !== "soldout" && <PriceButton tokens={null} label={h.unlock} testId={`demo-drop-buy-${dr.id}`} variant="ghost" />}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold text-ink">{h.feedTitle}</h2>
        <p className="text-xs text-mist">{h.feedSub}</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {FEED.map((p, i) => (
            <motion.article key={p.id} data-testid={`demo-feed-${p.id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05, duration: 0.6, ease }} className="card-soft overflow-hidden">
              <div className={`relative h-44 bg-gradient-to-br ${p.creator.grad}`}>
                {!p.unlocked && <div className="absolute inset-0 flex items-center justify-center backdrop-blur-md"><Lock size={22} className="text-white" /></div>}
                <span className="eyebrow absolute left-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[0.55rem] text-ink">{t.demo.types[p.type]}</span>
                {p.episode && <span className="absolute right-3 top-3 rounded-full bg-ink/70 px-2 py-0.5 font-mono text-[0.6rem] text-white">{p.episode}</span>}
                {p.pieces && <span className="absolute right-3 top-3 rounded-full bg-ink/70 px-2 py-0.5 font-mono text-[0.6rem] text-white">{p.pieces} pcs</span>}
              </div>
              <div className="p-4">
                <CreatorChip c={p.creator} small />
                <p className="font-display mt-2 text-sm font-bold text-ink">{p.title[lang]}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-mist">{p.price ? <><Coins size={11} className="mr-1 inline text-amber-500" />{p.price} · {usd(p.price)}</> : p.sub ? h.subscribers : ""}</span>
                  {p.unlocked ? <span className="text-xs font-semibold text-violet-700">✓ {h.unlocked}</span> : p.sub ? <PriceButton label={h.subscribe} testId={`demo-feed-sub-${p.id}`} /> : <PriceButton tokens={p.price} label={h.unlock} testId={`demo-feed-unlock-${p.id}`} />}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export const DemoDiscover = () => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");
  const list = CREATORS.filter((c) => (tag === "all" || c.tag === tag) && (c.name + c.handle + c.tag).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="demo-discover" className="pb-20">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">{t.discover.title}</h1>
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <label className="flex w-full items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 lg:max-w-sm"><Search size={16} className="text-mist" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.discover.search} className="w-full bg-transparent text-sm outline-none" /></label>
        <div className="flex flex-wrap gap-1.5">{["all", ...CREATOR_TAGS].map((tg) => <button key={tg} onClick={() => setTag(tg)} className={`pill px-3 py-1.5 text-xs font-semibold ${tag === tg ? "bg-ink text-white" : "border border-line bg-white text-ink-2"}`}>{tg === "all" ? t.discover.all : tg}</button>)}</div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {list.map((c) => (
          <article key={c.id} data-testid={`demo-creator-${c.id}`} className="card-soft overflow-hidden">
            <div className={`h-24 bg-gradient-to-br ${c.grad}`} />
            <div className="px-4 pb-4">
              <div className={`-mt-7 flex h-14 w-14 items-center justify-center rounded-2xl border-4 border-white bg-gradient-to-br ${c.grad} font-display font-extrabold text-white`}>{initials(c.name)}</div>
              <p className="font-display mt-2 font-bold text-ink">{c.name} <BadgeCheck size={14} className="inline text-violet-600" /></p>
              <p className="text-xs text-mist">{c.handle} · {c.tag} · {c.fans} fans</p>
              <p className="mt-2 text-xs text-ink-2">{c.bio[lang]}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => toast(t.demo.toastPay)} className="pill btn-ghost flex-1 py-2 text-xs font-semibold">{t.discover.follow}</button>
                <button onClick={() => toast(t.demo.toastPay)} className="pill btn-primary flex-1 py-2 text-xs font-semibold">{t.discover.subscribe} · $9.99</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
